'use client';
import clsx from 'clsx';
import { leagueStyles } from './league.style';
import { ReactNode, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAppearanceStore } from '@/store/appearance';
import { Match } from '../match';
import { NewbieMatch } from '../newbie-match';
import { cx } from 'styled-system/css';
import { SportMatchModel } from '@nf/types/odds';
import { MyFavouritesLeagueButton } from '@/features/my-favourites/components/my-favourites-league-button';
import { MarketRefresh } from '@/features/advanced-options/sports-tool-bar/components/market-refresh';
import { LeagueIcon } from '@/features/odds-page/league-icon';
import { useOddsTableMarsContext } from '../../context';
import { useAccountInfo } from '@/hooks/query/account/use-account-info';
import { Icon } from '@/components/icons/svg-icons';
import { useGoldenDuelSetting } from '@/hooks/query/golden-duel';
import { goldenDuelModalService } from '@/services/golden-duel/golden-duel-modal.service';
import { useThrottleClick } from '@nf/utils-common/hooks';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';

export interface LeagueProps {
	leagueId: number;
	leagueName: string;
	matches: Array<SportMatchModel>;
	isAllLeagueExpand: boolean;
	expandMatchCount: number;
	leagueGroupId?: number;
	parentMatchId?: number;
}
export const League = (props: LeagueProps) => {
	const { isGoldenDuelSupported } = useGoldenDuelSetting();
	const { sport, gameType } = useOddsTableMarsContext();
	const { leagueId, leagueName, matches, isAllLeagueExpand, expandMatchCount, leagueGroupId, parentMatchId } = props;
	const { user } = useAccountInfo();
	const hasOddsBoostPromo = (Boolean(user?.PromotionOddsBoostInfo?.EnablePromotionOddsBoost) && leagueId === 3) ||
		(Boolean(user?.PromotionOddsBoostInfo?.EnableAffPromotionOddsBoost) && leagueId === 155467);
	const [isLeagueExpand, setIsLeagueExpand] = useState<boolean>(isAllLeagueExpand);
	const [oddsPageLayout] = useAppearanceStore((state) => [state.oddsPageLayout, state.fontSize]);
	const isNewbieTheme = oddsPageLayout === 'list';
	const isGoldenDuel = isGoldenDuelSupported(sport, leagueId ?? 0);

	useEffect(() => {
		setIsLeagueExpand(isAllLeagueExpand);
	}, [isAllLeagueExpand]);

	// match group 顯示 或 隱藏
	const toggleLeague = () => {
		setIsLeagueExpand(!isLeagueExpand);
	}

	const setKafka = () => {
		const kafkaData = {
			note: KafkaNote.GoldenDuel,
			type: 1,
			action: 10
		};
		setKafkaNormal(kafkaData);
	}

	const clickDetailIcon = useThrottleClick(() => {
		setKafka();
		goldenDuelModalService.displayGoldenDuelRulesModal(true);
	}, 300);

	return (
		<div className={cx('league', leagueStyles.league)}>
			<div
				className={clsx(leagueStyles.header, { [leagueStyles.oddsBoostPromo]: hasOddsBoostPromo })}
				onClick={toggleLeague}
			>
				<LeagueIcon
					leagueId={leagueId}
					sport={sport}
					parentMatchId={parentMatchId}
					enableOddsBoostPromo={hasOddsBoostPromo}
					isGoldenDuel={isGoldenDuel}
					goldenDuelTag={'normal'}
				/>
				<div className={leagueStyles.nameWrapper}>
					<div
						className={clsx({
							[leagueStyles.nbaTag]: isGoldenDuel,
							[leagueStyles.text]: !isGoldenDuel
						})}
					>
						{sport === 999 ? `[${matches[0].Etm.substring(0, 10).replace(/-/g, '/')}] ` : ''}
						{leagueName}
					</div>
					{
						isGoldenDuel ? (
							<button className={leagueStyles.detailWrapper} onClick={clickDetailIcon}>
								<Icon name="details" className={leagueStyles.nbaDetail} />
							</button>
						) : null
					}
				</div>

				{isLeagueExpand ? <MarketRefresh /> : null}
				{
					sport !== 999 ?
						<div
							className={leagueStyles.badge}
							data-show={!isLeagueExpand}
						>
							{matches.length}
						</div> : null
				}
				<MyFavouritesLeagueButton
					leagueId={leagueId}
					sportType={sport}
				/>
			</div>

			<motion.div
				className={cx('match-group', leagueStyles.matchGroup)}
				initial="open"
				animate={isLeagueExpand ? 'open' : 'closed'}
				variants={{
					open: { height: 'auto', opacity: 1 },
					closed: { height: 0, opacity: 0 }
				}}
			>
				{
					matches.map((match: SportMatchModel, idx: number): ReactNode => {
						return isNewbieTheme && gameType !== 'FC' ? (
							<NewbieMatch
								key={match.MatchId}
								match={match}
								isDefaultExpand={
									sport === 999 ?
										(isAllLeagueExpand || isLeagueExpand)
										: expandMatchCount ? idx < expandMatchCount : false
								}
							/>
						) : (
							<Match
								key={match.MatchId}
								match={match}
								isDefaultExpand={
									sport === 999 ?
										(isAllLeagueExpand || isLeagueExpand)
										: expandMatchCount ? idx < expandMatchCount : false
								}
								isLeagueExpand={isLeagueExpand}
							/>
						)
					})
				}
			</motion.div>
		</div>
	)
}